import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _filter from 'lodash/filter';
import _get from 'lodash/get';
import { Editor, RichUtils, EditorState, Modifier, CompositeDecorator, convertFromRaw, convertToRaw, ContentState } from 'draft-js';
import createStyles from 'draft-js-custom-styles';
import 'draft-js/dist/Draft.css';
import { EMPTY_ARRAY } from '@tekion/tekion-base/app.constants';
import StyleControls from './styleControls';
import { getBlockRendererFn } from './RichTextEditor.renderConfig';
import { blockStyleFn, clearFormatting, getLinkFromState } from './helpers/rickTextEditor.helpers';
import CONTROLS from './controls.constants';
import s from './richTextEditor.module.scss';
var styleMap = {
  STRIKETHROUGH: {
    textDecoration: 'line-through'
  }
};
var EXTRA_CONTROLS = [{
  label: __('Insert Table'),
  style: 'insertTable',
  type: 'dropdown',
  icon: 'icon-table1'
}, {
  label: __('Line Height'),
  style: 'changeLineHeight',
  type: 'dropdown',
  icon: 'icon-line-height'
}];
var RichTextEditor = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(RichTextEditor, _React$PureComponent);
  var _super = _createSuper(RichTextEditor);
  function RichTextEditor(props) {
    var _this;
    _classCallCheck(this, RichTextEditor);
    _this = _super.call(this, props);
    _defineProperty(_assertThisInitialized(_this), "handleKeyCommand", function (command, newState) {
      var updatedState = RichUtils.handleKeyCommand(newState, command);
      if (updatedState) {
        _this.setEditorInternalState(updatedState);
        return true;
      }
      return false;
    });
    _defineProperty(_assertThisInitialized(_this), "onAddLink", function () {
      var editorInternalState = _this.state.editorInternalState;
      var selection = editorInternalState.getSelection();
      if (selection.isCollapsed()) return false;
      var url = getLinkFromState(editorInternalState);
      _this.setState({
        link: url,
        showLinkDialog: true
      });
      return true;
    });
    _defineProperty(_assertThisInitialized(_this), "onClearFormatting", function () {
      var editorInternalState = _this.state.editorInternalState;
      var updatedState = clearFormatting(editorInternalState);
      if (updatedState) {
        _this.setEditorInternalState(updatedState);
        return true;
      }
      return false;
    });
    _defineProperty(_assertThisInitialized(_this), "onUndo", function () {
      var editorInternalState = _this.state.editorInternalState;
      _this.setEditorInternalState(EditorState.undo(editorInternalState));
    });
    _defineProperty(_assertThisInitialized(_this), "onRedo", function () {
      var editorInternalState = _this.state.editorInternalState;
      _this.setEditorInternalState(EditorState.redo(editorInternalState));
    });
    _defineProperty(_assertThisInitialized(_this), "toggleOpen", function () {
      _this.setState(function (prevState) {
        return {
          showLinkDialog: !prevState.showLinkDialog
        };
      });
    });
    _defineProperty(_assertThisInitialized(_this), "onUpdateClick", function () {
      var _this$state = _this.state,
        editorInternalState = _this$state.editorInternalState,
        link = _this$state.link;
      var selection = editorInternalState.getSelection();
      var content = editorInternalState.getCurrentContent();
      if (!link) {
        _this.setEditorInternalState(RichUtils.toggleLink(editorInternalState, selection, null));
        return;
      }
      var contentWithEntity = content.createEntity('LINK', 'MUTABLE', {
        url: link
      });
      var newEditorState = EditorState.push(editorInternalState, contentWithEntity, 'create-entity');
      var entityKey = contentWithEntity.getLastCreatedEntityKey();
      _this.setState({
        editorInternalState: RichUtils.toggleLink(newEditorState, selection, entityKey),
        link: '',
        showLinkDialog: false
      });
    });
    _defineProperty(_assertThisInitialized(_this), "renderControls", function (styles) {
      var _this$props = _this.props,
        readOnly = _this$props.readOnly,
        extraControls = _this$props.extraControls;
      if (readOnly) {
        return null;
      }
      var editorInternalState = _this.state.editorInternalState;
      var CUSTOM_CONTROL_HANDLERS = {
        link: _this.onAddLink,
        clearFormatting: _this.onClearFormatting,
        undo: _this.onUndo,
        redo: _this.onRedo
      };
      return /*#__PURE__*/React.createElement("div", {
        className: s.controls
      }, /*#__PURE__*/React.createElement(StyleControls, {
        styles: styles,
        onChange: _this.setEditorInternalState,
        controls: _this.controls,
        editorState: editorInternalState,
        customHandlers: CUSTOM_CONTROL_HANDLERS,
        extraControls: extraControls
      }));
    });
    // eslint-disable-next-line consistent-return
    _defineProperty(_assertThisInitialized(_this), "handlePastedText", function (text) {
      var editorInternalState = _this.state.editorInternalState;
      // when pasting into a table cell only allow plain text
      // to be inserted or the table will become corrupted
      var content = editorInternalState.getCurrentContent();
      var selection = editorInternalState.getSelection();
      var block = content.getBlockForKey(selection.getStartKey());
      if (block.getType() === 'table') {
        if (selection.isCollapsed()) {
          content = Modifier.insertText(content, selection, text);
        } else {
          content = Modifier.replaceText(content, selection, text);
        }
        _this.setEditorInternalState(EditorState.push(editorInternalState, content, 'insert-characters'));
        return true;
      }
    });
    _defineProperty(_assertThisInitialized(_this), "handleReturn", function (e, editorInternalState) {
      var _editorInternalState$, _editorInternalState$2;
      var selection = editorInternalState.getSelection();
      if ((editorInternalState === null || editorInternalState === void 0 ? void 0 : (_editorInternalState$ = editorInternalState.getCurrentContent()) === null || _editorInternalState$ === void 0 ? void 0 : (_editorInternalState$2 = _editorInternalState$.getBlockForKey(selection === null || selection === void 0 ? void 0 : selection.getAnchorKey())) === null || _editorInternalState$2 === void 0 ? void 0 : _editorInternalState$2.getType()) === 'table') {
        _this.setEditorInternalState(RichUtils.insertSoftNewline(editorInternalState));
        return 'handled';
      }
      return 'not-handled';
    });
    _defineProperty(_assertThisInitialized(_this), "handleOnBlur", function () {
      var handleChange = _this.props.handleChange;
      var editorInternalState = _this.state.editorInternalState;
      handleChange(editorInternalState);
    });
    _defineProperty(_assertThisInitialized(_this), "setEditorInternalState", function (value) {
      _this.setState({
        editorInternalState: value
      });
    });
    _this.state = {
      link: '',
      showLinkDialog: false,
      editorInternalState: props.editorState
    };
    _this.editor = /*#__PURE__*/React.createRef();
    _this.editorContainer = /*#__PURE__*/React.createRef();
    _this.controls = [].concat(_toConsumableArray(_filter(CONTROLS, function (control) {
      return _get(control, 'label') !== 'Link';
    })), EXTRA_CONTROLS);
    return _this;
  }
  _createClass(RichTextEditor, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var editorInternalState = this.state.editorInternalState;
      this.setEditorInternalState(EditorState.forceSelection(editorInternalState, editorInternalState.getSelection()));
    }
  }, {
    key: "render",
    value: function render() {
      var editorInternalState = this.state.editorInternalState;
      var _this$props2 = this.props,
        readOnly = _this$props2.readOnly,
        className = _this$props2.className,
        showScroll = _this$props2.showScroll;
      var blockRendererFn = getBlockRendererFn(this.editorContainer.current, readOnly);
      var _createStyles = createStyles(['font-size', 'color']),
        styles = _createStyles.styles,
        customStyleFn = _createStyles.customStyleFn;
      var wrapperClassName = readOnly ? s.textAreaReadOnly : s.textArea;
      return /*#__PURE__*/React.createElement("div", {
        className: cx(s.editor, className)
      }, /*#__PURE__*/React.createElement("div", {
        className: showScroll ? wrapperClassName : '',
        ref: this.editorContainer
      }, /*#__PURE__*/React.createElement(Editor, {
        ref: this.editor,
        editorState: editorInternalState,
        onBlur: this.handleOnBlur,
        onChange: this.setEditorInternalState,
        blockRendererFn: blockRendererFn,
        handleKeyCommand: this.handleKeyCommand,
        customStyleMap: styleMap,
        customStyleFn: customStyleFn,
        blockStyleFn: blockStyleFn,
        handleReturn: this.handleReturn,
        handlePastedText: this.handlePastedText,
        readOnly: readOnly
      })), this.renderControls(styles));
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(nextProps) {
      var editorState = nextProps.editorState,
        _nextProps$readOnly = nextProps.readOnly,
        readOnly = _nextProps$readOnly === void 0 ? false : _nextProps$readOnly;
      if (!readOnly) {
        return null;
      }
      return {
        editorInternalState: EditorState.forceSelection(editorState, editorState.getSelection(editorState))
      };
    }
  }]);
  return RichTextEditor;
}(React.PureComponent);
RichTextEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  className: PropTypes.string,
  showScroll: PropTypes.bool,
  extraControls: PropTypes.array
};
RichTextEditor.defaultProps = {
  className: '',
  readOnly: false,
  showScroll: true,
  extraControls: EMPTY_ARRAY
};
export default RichTextEditor;
export { Editor, RichUtils, EditorState, Modifier, CompositeDecorator, convertFromRaw, convertToRaw, ContentState };